import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Code2, Copy, ExternalLink, Globe, Info, Loader2, RefreshCw,
  Sparkles, Trash2,
} from 'lucide-react';
import { websiteApi } from '../api';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

const TONES = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly & warm' },
  { value: 'luxury', label: 'Luxury / premium' },
  { value: 'casual', label: 'Casual' },
];

export default function WebsiteTab({ branchParam }) {
  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tone, setTone] = useState('professional');
  const [generating, setGenerating] = useState(false);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await websiteApi.get();
      setWebsite(data.website || null);
      if (data.website?.tone) setTone(data.website.tone);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load business website');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const embedUrl = useMemo(() => (
    website?.embed_token
      ? `${window.location.origin}/site/${website.embed_token}`
      : ''
  ), [website?.embed_token]);

  const iframeCode = useMemo(() => {
    if (!embedUrl) return '';
    const title = website?.content?.business_name || 'Business website';
    return `<iframe\n  src="${embedUrl}"\n  title="${title.replace(/"/g, '&quot;')}"\n  width="100%"\n  height="850"\n  style="width:100%;border:0;display:block;"\n  loading="lazy"\n  referrerpolicy="strict-origin-when-cross-origin"\n></iframe>`;
  }, [embedUrl, website?.content?.business_name]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await websiteApi.generate({
        branch_id: branchParam?.branch_id || null,
        tone,
      });
      setWebsite(data);
      toast.success(website ? 'Business website regenerated' : 'Business website generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate business website');
    } finally {
      setGenerating(false);
    }
  };

  const copy = async (value, message) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(message);
    } catch {
      toast.error('Unable to copy. Select the code and copy it manually.');
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete the generated business website and invalidate its iframe?')) return;
    setRemoving(true);
    try {
      await websiteApi.remove();
      setWebsite(null);
      toast.success('Generated website deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete website');
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-16">
        <Loader2 size={22} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="website-tab">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope' }}>
                <Globe size={16} className="text-primary" /> Embeddable Business Website
              </CardTitle>
              <p className="mt-1 max-w-3xl text-xs leading-relaxed text-muted-foreground">
                Generate a responsive one-page website from the connected Google Business Profile, then copy one iframe snippet into any existing website. No subdomain or DNS configuration is required.
              </p>
            </div>
            {website?.content && (
              <Badge className="border border-emerald-500/20 bg-emerald-500/10 text-emerald-700">Ready to embed</Badge>
            )}
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Sparkles size={14} className="text-primary" />
            {website?.content ? 'Regenerate website' : 'Generate website'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label className="mb-1.5 block text-xs">Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger data-testid="website-tone-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TONES.map((item) => (
                    <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2 sm:col-span-2">
              <Button onClick={generate} disabled={generating} data-testid="website-generate-btn">
                {generating
                  ? <Loader2 size={14} className="mr-2 animate-spin" />
                  : website?.content
                    ? <RefreshCw size={14} className="mr-2" />
                    : <Sparkles size={14} className="mr-2" />}
                {website?.content ? 'Regenerate from Google Business' : 'Generate from Google Business'}
              </Button>
              {website?.content && (
                <Button variant="outline" onClick={remove} disabled={removing} className="text-destructive">
                  {removing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Trash2 size={14} className="mr-2" />}
                  Delete
                </Button>
              )}
            </div>
          </div>
          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info size={12} className="mt-0.5 shrink-0" />
            Regeneration keeps the same iframe URL, so existing website integrations do not need to be updated.
          </p>
        </CardContent>
      </Card>

      {website?.content && embedUrl && (
        <>
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Globe size={14} /> Live preview
                </CardTitle>
                <a href={embedUrl} target="_blank" rel="noreferrer">
                  <Button variant="outline" size="sm" className="gap-1.5">
                    <ExternalLink size={13} /> Open full page
                  </Button>
                </a>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-hidden rounded-xl border bg-background">
                <iframe
                  src={embedUrl}
                  title={`${website.content?.business_name || 'Business'} website preview`}
                  className="h-[560px] w-full border-0"
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  data-testid="website-preview-frame"
                />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="website-iframe-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Code2 size={14} className="text-primary" /> Iframe embed code
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs leading-relaxed text-muted-foreground">
                Paste this code into an HTML block on WordPress, Wix, Shopify, Webflow, or any custom website. Adjust the height value when required by the page layout.
              </p>
              <textarea
                readOnly
                value={iframeCode}
                rows={9}
                onFocus={(event) => event.target.select()}
                className="w-full resize-y rounded-lg border bg-muted/30 p-3 font-mono text-xs leading-relaxed outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="website-iframe-code"
              />
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => copy(iframeCode, 'Iframe code copied')} className="gap-2" data-testid="website-copy-iframe">
                  <Copy size={14} /> Copy iframe code
                </Button>
                <Button variant="outline" onClick={() => copy(embedUrl, 'Preview URL copied')} className="gap-2">
                  <Copy size={14} /> Copy preview URL
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {!website?.content && (
        <div className="rounded-lg border border-dashed bg-muted/30 p-8 text-center" data-testid="website-empty">
          <Globe size={30} className="mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm font-medium">No website generated yet</p>
          <p className="mt-1 text-xs text-muted-foreground">Generate it from the connected Google Business Profile, then copy the iframe code.</p>
        </div>
      )}
    </div>
  );
}
